import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { ArrowLeft, Printer, Download, Truck, ExternalLink, Sparkles, Mail } from "lucide-react";
import OrderTimeline from "@/components/OrderTimeline";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const statusStyles = {
  pending_payment: "bg-bone text-stone border-stone",
  paid: "bg-oxblood text-bone border-oxblood",
  shipped: "bg-charcoal text-bone border-charcoal",
  delivered: "bg-white text-oxblood border-oxblood",
  cancelled: "bg-borderSubtle text-stone border-borderSubtle line-through",
  abandoned: "bg-borderSubtle text-stone border-borderSubtle",
};

const statusLabel = {
  pending_payment: "Awaiting payment",
  paid: "Paid",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  abandoned: "Abandoned",
};

// Public tracking URLs — buyers can click straight to the carrier's page
const trackingUrl = (carrier, number) => {
  if (!number) return null;
  const encoded = encodeURIComponent(number.trim());
  switch ((carrier || "").toLowerCase()) {
    case "usps":
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`;
    case "ups":
      return `https://www.ups.com/track?tracknum=${encoded}`;
    case "fedex":
      return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`;
    case "dhl":
      return `https://www.dhl.com/en/express/tracking.html?AWB=${encoded}`;
    default:
      return null;
  }
};

export default function OrderDetail() {
  // iter148s — inherit Select cream palette when the buyer's flow came from
  // Select (same pattern as Contact + Orders list).
  const [tierPref] = useState(() => {
    try { return (localStorage.getItem("bbk_last_tier") || "supply").toLowerCase(); }
    catch { return "supply"; }
  });
  useSetSelectTheme(tierPref === "select");

  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrder(data);
      } catch {
        setErr("Order not found or unavailable.");
      }
    })();
  }, [orderId]);

  useEffect(() => {
    if (order) document.title = `BBK · Order · ${order.order_number}`;
    return () => { document.title = "Bulk Buy Kits"; };
  }, [order]);

  if (err) return <div className="p-12 overline" data-testid="order-detail-error">{err}</div>;
  if (!order) return <div className="p-12 overline">Loading…</div>;

  const addr = order.address || {};
  const trackUrl = trackingUrl(order.tracking_carrier, order.tracking_number);
  const totalKits = order.items.reduce((n, i) => n + i.quantity, 0);

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-12 py-10 lg:py-14" data-testid={`order-detail-${order.order_number}`}>
      {/* Top nav */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <Link to="/orders" className="text-[13px] uppercase tracking-[0.22em] inline-flex items-center gap-2 text-stone hover:text-charcoal">
          <ArrowLeft size={12} strokeWidth={1.5}/> Back to orders
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/orders/${order.id}/slip`}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.22em] text-xs uppercase transition-colors rounded-full"
            data-testid="view-slip-btn"
          >
            <Printer size={12} strokeWidth={2}/> View slip
          </Link>
          <a
            href={`${BACKEND}/api/orders/${order.id}/slip.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-borderSubtle hover:border-oxblood text-charcoal hover:text-oxblood tracking-[0.22em] text-xs uppercase transition-colors rounded-full"
            data-testid="download-slip-pdf-btn"
          >
            <Download size={12} strokeWidth={2}/> PDF
          </a>
        </div>
      </div>

      {/* Header block */}
      <div className="border-b border-charcoal pb-8 mb-8">
        <p className="overline mb-3">Order</p>
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">#{order.order_number}</h1>
            <p className="text-xs text-stone font-mono uppercase tracking-widest mt-3">
              Placed {new Date(order.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              {" · "}
              {totalKits} kit{totalKits > 1 ? "s" : ""}
              {" · "}
              {order.payment_method?.toUpperCase()}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span
              className={`overline px-3 py-1.5 border ${statusStyles[order.status]}`}
              data-testid="order-detail-status"
            >
              {statusLabel[order.status]}
            </span>
            <p className="font-mono text-3xl text-oxblood">${order.total.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* iter_133 — "You saved" banner. Lands before the numbers do. */}
      {(() => {
        const totalKitSavings = order.items.reduce((s, i) => s + Number(i.kit_savings ?? 0) * Number(i.quantity ?? 1), 0);
        const codeDiscount = Number(order.discount ?? 0);
        const totalSavings = totalKitSavings + codeDiscount;
        if (totalSavings < 0.01) return null;
        return (
          <div
            className="mb-8 border border-oxblood border-l-4 bg-[#FDF6E3] px-6 py-4 flex items-baseline justify-between gap-4 flex-wrap"
            data-testid="order-savings-banner"
          >
            <p className="font-serif text-oxblood text-xl md:text-2xl leading-none">
              You saved <span className="font-mono font-semibold" data-testid="order-savings-amount">${totalSavings.toFixed(2)}</span> on this order.
            </p>
            <p className="text-[11px] font-mono uppercase tracking-widest text-oxblood/80">
              {totalKitSavings > 0.005 && `Kits −$${totalKitSavings.toFixed(2)}`}
              {totalKitSavings > 0.005 && codeDiscount > 0.005 && "  ·  "}
              {codeDiscount > 0.005 && `Code${order.discount_code ? ` (${order.discount_code})` : ""} −$${codeDiscount.toFixed(2)}`}
            </p>
          </div>
        );
      })()}

      {/* Two-column: Timeline (dominant) + Ship-to / Payment / Tracking */}
      <div className="grid md:grid-cols-[1.3fr_1fr] gap-8 mb-8">
        <div>
          <OrderTimeline order={order} />
        </div>
        <div className="space-y-6">
          {/* Tracking */}
          {order.tracking_number ? (
            <div className="border border-oxblood/40 bg-white p-6" data-testid="tracking-block">
              <p className="overline text-oxblood mb-3 flex items-center gap-2"><Truck size={12} strokeWidth={2}/> Tracking</p>
              <p className="text-xs text-stone mb-1">{order.tracking_carrier || "Carrier"}</p>
              <p className="font-mono text-sm text-charcoal break-all mb-3" data-testid="tracking-number">{order.tracking_number}</p>
              {trackUrl && (
                <a
                  href={trackUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.22em] text-oxblood hover:text-charcoal"
                  data-testid="tracking-link"
                >
                  Track on {order.tracking_carrier} <ExternalLink size={10} strokeWidth={2}/>
                </a>
              )}
            </div>
          ) : (order.status === "shipped" || order.status === "delivered") ? (
            <div className="border border-borderSubtle bg-white p-6 text-center">
              <p className="overline text-stone mb-2"><Truck size={12} strokeWidth={2} className="inline mr-1.5"/> Tracking pending</p>
              <p className="text-xs text-stone leading-relaxed">Tracking will appear here shortly. Reach out to <a href="mailto:support@bulkbuykits.com" className="text-oxblood hover:text-charcoal">support@bulkbuykits.com</a> if it doesn't.</p>
            </div>
          ) : null}

          {/* Ship-to */}
          <div className="border border-borderSubtle bg-white p-6">
            <p className="overline mb-3">Ship to</p>
            <p className="font-medium text-charcoal">{order.customer_name}</p>
            <p className="text-stone text-sm">{order.customer_email}</p>
            <p className="text-sm text-charcoal mt-3 leading-relaxed">
              {addr.line1}
              {addr.line2 ? <><br/>{addr.line2}</> : null}
              <br/>
              {addr.city}{addr.city && addr.state ? ", " : ""}{addr.state} {addr.postal_code}
              <br/>
              {addr.country}
            </p>
          </div>

          {/* Payment info */}
          <div className="border border-borderSubtle bg-white p-6">
            <p className="overline mb-3">Payment</p>
            <p className="text-sm"><span className="text-stone">Method:</span> <span className="font-mono">{order.payment_method?.toUpperCase()}</span></p>
            {order.discount_code && (
              <p className="text-sm mt-1"><span className="text-stone">Code:</span> <span className="font-mono">{order.discount_code}</span></p>
            )}
            {order.status === "pending_payment" && (
              <Link
                to={`/payment/${order.id}`}
                className="inline-block mt-3 overline text-oxblood hover:text-charcoal"
                data-testid="pending-payment-link"
              >
                Complete payment →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Note to buyer */}
      {order.note_to_buyer && (
        <div className="mb-8 border-l-[3px] border-oxblood bg-white border border-borderSubtle px-6 py-5" data-testid="detail-note-to-buyer">
          <p className="overline text-oxblood mb-1.5">A note from Bulk Buy Kits</p>
          <p className="text-sm text-charcoal whitespace-pre-wrap leading-relaxed">{order.note_to_buyer}</p>
        </div>
      )}

      {/* Delivered nudge — asks for feedback. */}
      {order.status === "delivered" && (
        <div
          className="mb-8 relative overflow-hidden border border-oxblood/30 bg-white"
          data-testid="delivered-nudge"
        >
          <div className="absolute inset-y-0 left-0 w-1 bg-oxblood" aria-hidden />
          <div className="grid md:grid-cols-[1fr_auto] items-center gap-6 px-6 md:px-8 py-6 md:py-7">
            <div>
              <p className="overline text-oxblood mb-2 inline-flex items-center gap-1.5">
                <Sparkles size={12} strokeWidth={2}/> Delivered
              </p>
              <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-1.5">How did the kits arrive?</h3>
              <p className="text-sm text-stone leading-relaxed max-w-lg">
                A quick line back — even one sentence — helps us pack the next batch better.
              </p>
            </div>
            <div className="flex md:flex-col gap-2 md:items-stretch flex-wrap">
              <a
                href={`mailto:support@bulkbuykits.com?subject=${encodeURIComponent(`Feedback on order ${order.order_number}`)}&body=${encodeURIComponent(`Hi — quick note on order ${order.order_number}:\n\n`)}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-oxblood text-oxblood hover:bg-oxblood hover:text-bone tracking-[0.22em] text-xs uppercase transition-colors rounded-full whitespace-nowrap"
                data-testid="delivered-feedback-btn"
              >
                <Mail size={12} strokeWidth={2}/> Send feedback
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Items */}
      <div className="border border-borderSubtle bg-white overflow-x-auto mb-8" data-testid="detail-items-table">
        <table className="w-full text-sm">
          <thead className="border-b border-borderSubtle bg-bone">
            <tr className="text-left">
              <th className="overline px-6 py-3">Kit</th>
              <th className="overline px-6 py-3 text-right">Qty</th>
              <th className="overline px-6 py-3 text-right">Unit</th>
              <th className="overline px-6 py-3 text-right">Line</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((i, idx) => (
              <tr key={idx} className="border-b border-borderSubtle last:border-0" data-testid={`detail-item-${idx}`}>
                <td className="px-6 py-4 text-charcoal">{i.name}</td>
                <td className="px-6 py-4 text-right font-mono">{i.quantity}</td>
                <td className="px-6 py-4 text-right font-mono">${i.price.toFixed(2)}</td>
                <td className="px-6 py-4 text-right font-mono">${(i.price * i.quantity).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals — iter_133 retail / kit savings / code / shipping / total */}
      {(() => {
        const totalRetail = order.items.reduce((s, i) => s + Number(i.retail_line_price ?? i.price ?? 0) * Number(i.quantity ?? 1), 0);
        const totalKitSavings = order.items.reduce((s, i) => s + Number(i.kit_savings ?? 0) * Number(i.quantity ?? 1), 0);
        const codeDiscount = Number(order.discount ?? 0);
        const hasKitSavings = totalKitSavings > 0.005;
        const hasCodeDiscount = codeDiscount > 0.005;
        return (
          <div className="flex justify-end mb-10">
            <div className="w-full max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-stone">{hasKitSavings ? "Retail" : "Subtotal"}</span>
                <span className="font-mono">${(hasKitSavings ? totalRetail : Number(order.subtotal)).toFixed(2)}</span>
              </div>
              {hasKitSavings && (
                <div className="flex justify-between text-oxblood" data-testid="order-total-kit-savings">
                  <span>Multi-vial kit savings</span>
                  <span className="font-mono">−${totalKitSavings.toFixed(2)}</span>
                </div>
              )}
              {hasCodeDiscount && (
                <div className="flex justify-between text-oxblood" data-testid="order-total-code-discount">
                  <span>Discount code{order.discount_code ? ` (${order.discount_code})` : ""}</span>
                  <span className="font-mono">−${codeDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-stone">Shipping</span>
                <span className="font-mono">{Number(order.shipping || 0) <= 0 ? "FREE" : `$${Number(order.shipping).toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between border-t border-charcoal pt-2.5 mt-2 font-medium text-base">
                <span>Total</span><span className="font-mono text-oxblood">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      <p className="text-[12px] font-mono uppercase tracking-widest text-stone text-center border-t border-borderSubtle pt-6">
        Research use only · Not for human or animal consumption
      </p>
    </div>
  );
}
